/**
 * WEGO Favorites App - 店舗お気に入り機能
 * 店舗一覧ページ(/a/shops)のお気に入りボタン制御
 *
 * Version: 2026-01-30-vfa (v3)
 *
 * 必要なHTML要素:
 * - ボタン: .vfa-add-to-favorite-shop
 * - アイコン: .vfa-store-favorite-icon（SVGハート）
 * - テキスト: .vfa-store-favorite-text
 * - 属性: data-shop-name（必須）, data-shop-id（オプション）
 */

(function () {
  "use strict";

  console.log("[VFA Store Favorites] Script loaded - Version 2026-01-30-vfa");

  // APIエンドポイント（App Proxy経由）
  const STORE_API_BASE = "/apps/wego-favorite/api/stores/favorites";

  // デバッグモード（本番環境では false に設定）
  const DEBUG = false;

  // 設定
  const BUTTON_DISABLE_TIME = 1000; // 1秒間ボタン無効化
  const MAX_RETRIES = 3;
  const TEXT_ADD = "お気に入り登録";
  const TEXT_REMOVE = "お気に入り解除";

  // ボタンの色設定（本番デザイン準拠）
  const COLOR_FAVORITED = "#fa7283"; // お気に入り登録済みの色（本番準拠ピンク）
  const COLOR_DEFAULT = ""; // デフォルト色（CSSに任せる）

  /**
   * ログ出力
   */
  function log(...args) {
    if (DEBUG) {
      console.log("[VFA Store Favorites]", ...args);
    }
  }

  /**
   * 顧客IDを取得
   * @returns {string|null} 顧客ID（Shopify GID形式に変換）
   */
  function getCustomerId() {
    // Shopify Customerオブジェクトから取得
    if (window.ShopifyCustomer && window.ShopifyCustomer.id) {
      const id = String(window.ShopifyCustomer.id);
      return /^\d+$/.test(id) ? `gid://shopify/Customer/${id}` : id;
    }

    // __stオブジェクトから取得
    if (window.__st && window.__st.cid) {
      const id = String(window.__st.cid);
      return /^\d+$/.test(id) ? `gid://shopify/Customer/${id}` : id;
    }

    // メタタグから取得
    const metaCustomerId = document.querySelector(
      'meta[name="shopify-customer-id"]',
    );
    if (metaCustomerId && metaCustomerId.content) {
      const id = metaCustomerId.content;
      return /^\d+$/.test(id) ? `gid://shopify/Customer/${id}` : id;
    }

    return null;
  }

  /**
   * ログイン状態をチェック
   * @returns {boolean} ログイン済みかどうか
   */
  function isLoggedIn() {
    const customerId = getCustomerId();
    if (!customerId) {
      log("❌ 顧客IDが取得できません");
      return false;
    }
    log("✅ 顧客ID取得:", customerId);
    return true;
  }

  /**
   * 店舗お気に入り状態を確認
   * @param {string} storeName - 店舗名
   * @param {HTMLElement} button - ボタン要素
   */
  async function checkStoreFavoriteStatus(storeName, button) {
    if (!isLoggedIn()) {
      log("⏭️ 未ログインのため状態確認をスキップ");
      return;
    }

    try {
      // App Proxy経由の場合、customerIdをクエリパラメータとして追加しない
      // Shopify App Proxyが自動的にlogged_in_customer_idを追加するため
      const response = await fetch(STORE_API_BASE);
      if (response.ok) {
        const data = await response.json();
        const isFavorited = data.favorites?.some(
          (f) =>
            f.storeName === storeName ||
            f.storeHandle === button.dataset.shopId,
        );
        updateButtonState(button, isFavorited);
        log(
          "📊 店舗初期状態:",
          storeName,
          isFavorited ? "お気に入り済み" : "未登録",
        );
      } else {
        log("⚠️ 店舗状態確認エラー: HTTP", response.status);
      }
    } catch (error) {
      log("⚠️ 店舗状態確認エラー:", error);
    }
  }

  /**
   * 店舗お気に入り追加/削除
   * @param {HTMLElement} button - ボタン要素
   */
  async function toggleStoreFavorite(button) {
    const storeName = button.dataset.shopName;

    log("🖱️ 店舗クリック:", storeName);

    // ログインチェック
    if (!isLoggedIn()) {
      window.location.href =
        "/account/login?return_url=" +
        encodeURIComponent(window.location.pathname);
      return;
    }

    // 楽観的UI更新: 即座に状態を変更
    const isCurrentlyFavorited = button.classList.contains("vfa-registered");
    const newFavoritedState = !isCurrentlyFavorited;
    updateButtonState(button, newFavoritedState);

    // ボタンを無効化
    button.disabled = true;
    button.classList.add("vfa-is-loading");

    const action = isCurrentlyFavorited ? "remove" : "add";

    // リトライ機能付きでAPIリクエストを送信
    let retryCount = 0;
    let lastError = null;

    while (retryCount <= MAX_RETRIES) {
      try {
        // customerIdをリクエストボディに含める（フォールバック用）
        const customerId = getCustomerId();

        const response = await fetch(STORE_API_BASE, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            storeName: storeName,
            action: action,
            customerId: customerId, // フォールバック用
          }),
        });

        if (response.ok) {
          log(`✅ 店舗${action === "add" ? "追加" : "削除"}成功:`, storeName);
          break; // 成功したらループを抜ける
        } else {
          const errorData = await response.json().catch(() => ({}));

          // 認証エラーの場合はリトライしない
          if (response.status === 401 || response.status === 403) {
            alert("認証エラーが発生しました。ログインページへ移動します。");
            window.location.href =
              "/account/login?return_url=" +
              encodeURIComponent(window.location.pathname);
            updateButtonState(button, isCurrentlyFavorited);
            return;
          }

          // 店舗が見つからないエラーの場合はリトライしない
          if (
            response.status === 404 ||
            (errorData.error && errorData.error.includes("Store not found"))
          ) {
            log("❌ 店舗が見つかりません:", storeName);
            showErrorMessage("この店舗をお気に入りできません");
            updateButtonState(button, isCurrentlyFavorited);
            button.disabled = false;
            button.classList.remove("vfa-is-loading");
            return;
          }

          // サーバーエラー（5xx）の場合はリトライ
          if (response.status >= 500 && retryCount < MAX_RETRIES) {
            retryCount++;
            log(
              `⚠️ サーバーエラー (${response.status})。リトライ ${retryCount}/${MAX_RETRIES}...`,
            );
            await new Promise((resolve) =>
              setTimeout(resolve, 1000 * retryCount),
            );
            continue;
          }

          throw new Error(
            `API Error: ${response.status} - ${errorData.error || ""}`,
          );
        }
      } catch (error) {
        lastError = error;

        // ネットワークエラーの場合はリトライ
        if (
          retryCount < MAX_RETRIES &&
          (error.name === "TypeError" || error.message.includes("fetch"))
        ) {
          retryCount++;
          log(
            `⚠️ ネットワークエラー。リトライ ${retryCount}/${MAX_RETRIES}...`,
          );
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 * retryCount),
          );
          continue;
        }

        // リトライ上限に達した場合
        log("❌ 店舗エラー:", error);
        showErrorMessage(
          "お気に入りの更新に失敗しました。もう一度お試しください。",
        );
        // 失敗時は元に戻す
        updateButtonState(button, isCurrentlyFavorited);
        break;
      }
    }

    // 最終的にエラーが残っている場合
    if (lastError && retryCount >= MAX_RETRIES) {
      log("❌ 店舗エラー（リトライ上限）:", lastError);
      showErrorMessage(
        "エラーが発生しました。しばらく時間をおいてから再度お試しください。",
      );
      updateButtonState(button, isCurrentlyFavorited);
    }

    // 1秒後にボタンを有効化
    setTimeout(() => {
      button.disabled = false;
      button.classList.remove("vfa-is-loading");
    }, BUTTON_DISABLE_TIME);
  }

  /**
   * ボタンの状態を更新
   * @param {HTMLElement} button - ボタン要素
   * @param {boolean} isFavorited - お気に入り済みかどうか
   */
  function updateButtonState(button, isFavorited) {
    const textElement = button.querySelector(".vfa-store-favorite-text");

    if (isFavorited) {
      button.classList.add("vfa-registered");
      button.setAttribute("aria-pressed", "true");
      button.setAttribute("aria-label", "お気に入り解除");
      button.setAttribute("title", "お気に入り解除");
      if (textElement) textElement.textContent = TEXT_REMOVE;

      // ボタンの色を直接変更（CSSが設定されていない場合のフォールバック）
      button.style.backgroundColor = COLOR_FAVORITED;
      button.style.borderColor = COLOR_FAVORITED;
      button.style.color = "white";
    } else {
      button.classList.remove("vfa-registered");
      button.setAttribute("aria-pressed", "false");
      button.setAttribute("aria-label", "お気に入り登録");
      button.setAttribute("title", "お気に入り登録");
      if (textElement) textElement.textContent = TEXT_ADD;

      // ボタンの色を元に戻す
      button.style.backgroundColor = COLOR_DEFAULT;
      button.style.borderColor = COLOR_DEFAULT;
      button.style.color = COLOR_DEFAULT;
    }

    // アイコンの色を更新（SVGがある場合）
    const svgIcon = button.querySelector(".vfa-store-favorite-icon svg");
    if (svgIcon) {
      svgIcon.style.fill = isFavorited ? "white" : "#fa7283";
    }

    log("🔄 店舗状態更新:", button.dataset.shopName, isFavorited);
  }

  /**
   * エラーメッセージを表示
   */
  function showErrorMessage(message) {
    // 既存のエラー表示があれば削除
    const existingError = document.querySelector(".vfa-store-favorite-error");
    if (existingError) {
      existingError.remove();
    }

    // エラーメッセージ要素を作成
    const errorDiv = document.createElement("div");
    errorDiv.className = "vfa-store-favorite-error";
    errorDiv.textContent = message;
    errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background-color: #ff4444;
      color: white;
      padding: 12px 24px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    `;

    document.body.appendChild(errorDiv);

    // 3秒後に自動で消える
    setTimeout(() => {
      errorDiv.remove();
    }, 3000);
  }

  /**
   * 店舗お気に入りボタンを初期化
   */
  function initializeStoreFavoriteButtons() {
    const buttons = document.querySelectorAll(".vfa-add-to-favorite-shop");

    if (buttons.length === 0) {
      return;
    }

    log(`📍 店舗ボタン ${buttons.length}個を検出`);

    buttons.forEach((button, index) => {
      if (button.dataset.vfaInitialized === "true") {
        return;
      }

      const storeName = button.dataset.shopName;
      const shopId = button.dataset.shopId;

      if (!storeName) {
        log(
          `⚠️ 店舗ボタン${index + 1}: 店舗名（data-shop-name）が見つかりません`,
        );
        return;
      }

      log(
        `✅ 店舗ボタン${index + 1}: "${storeName}" (ID: ${shopId || "なし"})`,
      );

      button.dataset.vfaInitialized = "true";
      checkStoreFavoriteStatus(storeName, button);

      button.addEventListener(
        "click",
        function (e) {
          e.preventDefault();
          e.stopPropagation();
          toggleStoreFavorite(button);
        },
        true,
      );
    });
  }

  /**
   * 初期化実行
   */
  function init() {
    console.log("[VFA Store Favorites] 初期化開始");

    if (document.readyState === "loading") {
      document.addEventListener(
        "DOMContentLoaded",
        initializeStoreFavoriteButtons,
      );
    } else {
      setTimeout(initializeStoreFavoriteButtons, 100);
    }

    // 動的コンテンツ対応（MutationObserver）
    let debounceTimer;
    const observer = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(initializeStoreFavoriteButtons, 500);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  // 初期化開始
  init();

  // デバッグ用グローバル関数
  window.vfaStoreFavoriteDebug = {
    reinit: initializeStoreFavoriteButtons,
    getButtons: () =>
      document.querySelectorAll(
        '.vfa-add-to-favorite-shop[data-vfa-initialized="true"]',
      ),
    checkStatus: (storeName) => {
      const button = document.querySelector(`[data-shop-name="${storeName}"]`);
      if (button) checkStoreFavoriteStatus(storeName, button);
    },
    listStores: () => {
      const buttons = document.querySelectorAll(".vfa-add-to-favorite-shop");
      return Array.from(buttons).map((b) => ({
        name: b.dataset.shopName,
        id: b.dataset.shopId,
        favorited: b.classList.contains("vfa-registered"),
      }));
    },
  };
  log("📦 デバッグ関数が利用可能: window.vfaStoreFavoriteDebug");
})();
